import { useState, useEffect, useRef } from 'react';
import { COIN_ID_TO_FEED_ID, FEED_ID_TO_COIN_ID } from '@/utils/pyth-feeds';

const POLL_INTERVAL_MS = 5000;

interface PythParsedPrice {
  price: string;
  expo: number;
}

interface PythParsedEntry {
  id: string;
  price: PythParsedPrice;
}

interface PythHermesResponse {
  parsed: PythParsedEntry[];
}

export interface LivePythPricesResult {
  /** Map from coinId → live USD price */
  prices: Record<string, number>;
  /** Unix ms timestamp of the last successful fetch, or null if never fetched */
  lastUpdated: number | null;
}

/**
 * Polls Pyth Hermes every 5 seconds for live USD prices for the given coin IDs.
 *
 * @param coinIds - Array of coin IDs (e.g. ['btc', 'eth', 'sol']) to fetch
 * @param enabled - Set to false to pause polling (e.g. outside reveal/racing phases)
 */
export function useLivePythPrices(
  coinIds: string[],
  enabled: boolean
): LivePythPricesResult {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // Stable ref to avoid re-creating the interval when coinIds reference changes
  // while contents are the same
  const coinIdsKey = coinIds.slice().sort().join(',');
  const coinIdsKeyRef = useRef(coinIdsKey);
  coinIdsKeyRef.current = coinIdsKey;

  useEffect(() => {
    if (!enabled || coinIds.length === 0) {
      return;
    }

    async function fetchPrices() {
      const currentCoinIds = coinIdsKeyRef.current.split(',').filter(Boolean);
      if (currentCoinIds.length === 0) return;

      const params = new URLSearchParams();
      for (const coinId of currentCoinIds) {
        const feedId = COIN_ID_TO_FEED_ID[coinId];
        if (feedId) {
          params.append('ids[]', feedId);
        }
      }

      if (!params.has('ids[]')) return;

      try {
        const url = `https://hermes.pyth.network/v2/updates/price/latest?${params.toString()}`;
        const res = await fetch(url);
        if (!res.ok) return;

        const json: PythHermesResponse = await res.json();
        const updated: Record<string, number> = {};

        for (const entry of json.parsed ?? []) {
          const normalizedId = entry.id.startsWith('0x') ? entry.id : `0x${entry.id}`;
          const coinId = FEED_ID_TO_COIN_ID[normalizedId];
          if (!coinId) continue;
          const rawPrice = Number(entry.price.price);
          const expo = entry.price.expo;
          const usdPrice = rawPrice * Math.pow(10, expo);
          if (usdPrice > 0) {
            updated[coinId] = usdPrice;
          }
        }

        if (Object.keys(updated).length > 0) {
          setPrices((prev) => ({ ...prev, ...updated }));
          setLastUpdated(Date.now());
        }
      } catch {
        // Silently swallow fetch errors — the race UI should not crash if Pyth blips
      }
    }

    // Fetch immediately, then on interval
    fetchPrices();
    const interval = setInterval(fetchPrices, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
    };
    // Re-run when the sorted coin key or enabled state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinIdsKey, enabled]);

  return { prices, lastUpdated };
}
